import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Box,
  TextField,
  InputAdornment,
  Grid,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Close as CloseIcon, AutoAwesome as AutoAwesomeIcon, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material'
import {
  AI_REPLY_UNIT_PRICE_INR,
  GST_PERCENTAGE,
  MAX_TOPUP_UNITS,
} from '../../constants/payments'
import { calculateTopUpPricing } from '../../utils/paymentUtils'

export interface TopUpModalProps {
  open: boolean
  onClose: () => void
  onPurchase: (amount: number, units: number) => void
  loading: boolean
}

const PRESET_CREDITS = [1000, 3000, 5000, 10000, 20000, 30000]
const DEFAULT_PRESET_CREDITS = 3000

export const TopUpModal: React.FC<TopUpModalProps> = ({
  open,
  onClose,
  onPurchase,
  loading,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [inputValue, setInputValue] = useState<string>(DEFAULT_PRESET_CREDITS.toString())

  useEffect(() => {
    if (open) {
      setInputValue(DEFAULT_PRESET_CREDITS.toString())
    }
  }, [open])

  const parsedCredits = Number(inputValue)
  let errorText: string | null = null

  if (inputValue.trim() === '') {
    errorText = 'Please enter the number of AI reply credits.'
  } else if (isNaN(parsedCredits) || !Number.isInteger(parsedCredits) || parsedCredits < 1) {
    errorText = 'Credits must be a positive number.'
  } else if (parsedCredits > MAX_TOPUP_UNITS) {
    errorText = `Maximum limit is ${MAX_TOPUP_UNITS.toLocaleString('en-IN')} credits.`
  } else if (parsedCredits % 500 !== 0) {
    errorText = 'Credits must be in multiples of 500.'
  }

  const isValid = errorText === null
  const pricing = calculateTopUpPricing(isValid ? parsedCredits : 0)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handlePresetSelect = (preset: number) => {
    setInputValue(preset.toString())
  }

  const handleConfirm = () => {
    if (isValid) {
      onPurchase(pricing.totalAmount, parsedCredits)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          m: isMobile ? 0 : 2,
        },
      }}
    >
      <DialogTitle sx={{ p: isMobile ? 2.5 : 3, pb: 1, position: 'relative' }}>
        <Box sx={{ pr: 5 }}>
          <Typography
            variant="h6"
            fontWeight={900}
            sx={{ fontSize: isMobile ? '1.15rem' : '1.3rem', lineHeight: 1.2 }}
          >
            Top-Up Credits
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, fontWeight: 500, lineHeight: 1.45 }}
          >
            Purchase up to {MAX_TOPUP_UNITS.toLocaleString('en-IN')} AI reply credits.
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: isMobile ? 2.5 : 3, pt: 2, pb: 2.5 }}>
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="caption"
              fontWeight={800}
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                mb: 0.5,
                mt: 1,
                display: 'block',
                letterSpacing: '0.5px',
              }}
            >
              AI Reply Credits
            </Typography>
            <TextField
              type="number"
              fullWidth
              value={inputValue}
              onChange={handleInputChange}
              placeholder="e.g. 2000"
              error={Boolean(errorText)}
              helperText={errorText || 'Enter credits in multiples of 500'}
              FormHelperTextProps={{
                sx: {
                  color: errorText ? 'error.main' : 'text.secondary',
                  fontWeight: errorText ? 700 : 500,
                  fontSize: '0.8rem',
                  mt: 0.5,
                  mx: 0,
                  minHeight: '1.2em',
                },
              }}
              inputProps={{ min: 500, max: MAX_TOPUP_UNITS, step: 500 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  fontWeight: 900,
                  fontSize: isMobile ? '1.05rem' : '1.2rem',
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="caption"
              fontWeight={800}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', mb: 1, display: 'block' }}
            >
              Popular Credit Options
            </Typography>
            <Grid container spacing={1.5}>
              {PRESET_CREDITS.map((preset) => (
                <Grid item xs={6} sm={4} key={preset}>
                  <Button
                    fullWidth
                    variant={parsedCredits === preset ? 'contained' : 'outlined'}
                    color={parsedCredits === preset ? 'primary' : 'inherit'}
                    onClick={() => handlePresetSelect(preset)}
                    sx={{
                      py: 1,
                      borderRadius: 2.5,
                      fontWeight: 800,
                      textTransform: 'none',
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                    }}
                  >
                    {preset.toLocaleString('en-IN')} Credits
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: '#f8fafc',
              border: '1px solid',
              borderColor: '#e2e8f0',
              borderRadius: 3,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={800}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', mb: 1.5, display: 'block', letterSpacing: '0.5px' }}
            >
              Payment Breakdown
            </Typography>

            <Stack spacing={1.2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Base Amount
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.8rem' }}>
                    {isValid ? parsedCredits.toLocaleString('en-IN') : 0} × ₹{AI_REPLY_UNIT_PRICE_INR.toFixed(2)}/credit
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={800} color="text.primary" sx={{ whiteSpace: 'nowrap', mt: 0.2 }}>
                  ₹{pricing.baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  GST ({GST_PERCENTAGE}%)
                </Typography>
                <Typography variant="body2" fontWeight={800} color="text.primary">
                  ₹{pricing.gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={900} color="text.primary">
                    Total Payable Amount
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Valid for 6 months from purchase date
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={900} color="primary.main">
                  ₹{pricing.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                mt: 2,
                p: 1.25,
                px: 1.5,
                borderRadius: 2,
                bgcolor: '#eff6ff',
                border: '1px solid #dbeafe',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: '#2563eb', flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 600, fontSize: '0.75rem', lineHeight: 1.3 }}>
                Note: An active Growth Plan is required to use top-up credits.
              </Typography>
            </Box>
          </Paper>

        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: isMobile ? 2.5 : 3,
          pb: isMobile ? 2.5 : 3,
          pt: 0,
          flexDirection: isMobile ? 'column-reverse' : 'row',
          gap: isMobile ? 1 : 0,
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          disabled={loading}
          sx={{ fontWeight: 700, width: isMobile ? '100%' : 'auto' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !isValid}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            py: 1.5,
            px: 3,
            borderRadius: 3,
            fontWeight: 900,
            textTransform: 'none',
            width: isMobile ? '100%' : 'auto',
            background: 'linear-gradient(90deg, #2563eb 0%, #3b82f6 100%)',
          }}
        >
          Proceed to Pay
        </Button>
      </DialogActions>
    </Dialog>
  )
}
